// Lightweight reactive state shared between Phaser scenes and the Preact overlay.
// No external state library — Phaser owns gameplay, Preact owns modals, and
// they coordinate through this small bus.

import { db, ensureProgress, getSettings, type Profile, type Progress, type Settings } from '../data/db';

type Screen =
  | { kind: 'boot' }
  | { kind: 'profile-picker' }
  | { kind: 'academy' }
  | { kind: 'zone'; lessonId: string }
  | { kind: 'combat'; lessonId: string }
  | { kind: 'parent' }
  | { kind: 'ingest' };

export interface AppState {
  screen: Screen;
  profile: Profile | null;
  progress: Progress | null;
  settings: Settings | null;
}

type Listener = (s: AppState) => void;

class StateBus {
  private state: AppState = {
    screen: { kind: 'boot' },
    profile: null,
    progress: null,
    settings: null,
  };
  private listeners = new Set<Listener>();

  get(): AppState { return this.state; }

  on(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => { this.listeners.delete(fn); };
  }

  set(patch: Partial<AppState>): void {
    this.state = { ...this.state, ...patch };
    for (const fn of this.listeners) fn(this.state);
  }

  async boot(): Promise<void> {
    const settings = await getSettings();
    if (settings.selectedProfileId) {
      const profile = await db.profiles.get(settings.selectedProfileId);
      if (profile) {
        const progress = await ensureProgress(profile.id);
        this.set({ profile, progress, settings, screen: { kind: 'academy' } });
        return;
      }
    }
    this.set({ settings, screen: { kind: 'profile-picker' } });
  }

  async selectProfile(profileId: string): Promise<void> {
    const profile = await db.profiles.get(profileId);
    if (!profile) return;
    const progress = await ensureProgress(profileId);
    const settings = (await getSettings());
    settings.selectedProfileId = profileId;
    await db.settings.put(settings);
    this.set({ profile, progress, settings, screen: { kind: 'academy' } });
  }

  async refreshProgress(): Promise<void> {
    if (!this.state.profile) return;
    const progress = await ensureProgress(this.state.profile.id);
    this.set({ progress });
  }

  goto(screen: Screen): void { this.set({ screen }); }
}

export const appState = new StateBus();
